import React, { useEffect, useRef, useState } from 'react';
import { Wheat, Wine, Moon } from 'lucide-react';
import { MeatIcon } from '../../theme/CustomIcons';
import { SAGE_GREEN, LIVING_CORAL, SHELL_WHITE, TWILIGHT_NAVY, alpha } from '../../theme/colors';
import { DOT_CARDS } from './dotCalendarData';

const ICONS = { meat: MeatIcon, wheat: Wheat, wine: Wine, moon: Moon };

// Sizing for the two places this cluster appears: full-size floating over
// the desktop Hero image, and a shrunk-down version over the mobile Hero
// photo. Not a CSS scale — Paper's `transform: scale()` doesn't actually
// resize a cloned subtree, so this mockup carries its own literal sizes
// per variant instead of one base size scaled down.
// No hardcoded card width — it's derived from dot/gap/padding below so the
// card always fits its actual content (7 dots + 6 gaps + padding both
// sides). A fixed magic-number width here previously worked out close
// enough for desktop by coincidence, but was meaningfully wider than the
// mobile dot grid's real content width, leaving a visible gap on the right
// of every mobile card.
const SIZES = {
  desktop: { dot: 16, gap: 8, padding: 16, iconWrap: 24, iconSize: 16, labelSize: 15, clusterGap: 16 },
  mobile: { dot: 9, gap: 4, padding: 10, iconWrap: 18, iconSize: 11, labelSize: 11, clusterGap: 10 },
};

// Two-stage reveal, both driven by a single IntersectionObserver per card
// (no per-dot state): the card itself fades/slides in first, staggered by
// its index; then every dot ripples in in quick succession via plain CSS
// animation-delay off a shared keyframe. Dots use animation-play-state to
// stay frozen on their 0%-keyframe (opacity 0) until the card's own
// observer fires, rather than a per-dot visibility flag.
const DOT_POP_KEYFRAMES = `
@keyframes dotCalendarPop {
  from { opacity: 0; transform: scale(0.3); }
  to { opacity: 1; transform: scale(1); }
}`;

const CARD_STAGGER = 0.2; // seconds between each card's own entrance
const CARD_FADE_DURATION = 0.7; // seconds for the card's own fade/slide
const DOT_STAGGER = 0.02; // seconds between each dot popping in

const DotCard = ({ card, s, index, startDelay }) => {
  const Icon = ICONS[card.icon];
  const accent = card.isNegative ? LIVING_CORAL : SAGE_GREEN;
  const rows = [];
  for (let i = 0; i < card.values.length; i += 7) rows.push(card.values.slice(i, i + 7));
  const isLarge = s.padding >= 14;
  const cardWidth = s.dot * 7 + s.gap * 6 + s.padding * 2;

  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStarted(true);
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const cardDelay = startDelay + index * CARD_STAGGER;
  const dotsStartAt = cardDelay + CARD_FADE_DURATION;

  return (
    <div
      ref={ref}
      style={{
        width: cardWidth,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        padding: s.padding,
        borderRadius: isLarge ? 12 : 9,
        gap: isLarge ? 12 : 7,
        boxShadow: `0 ${isLarge ? 12 : 6}px ${isLarge ? 28 : 16}px ${alpha('navy', isLarge ? 0.14 : 0.16)}`,
        backgroundColor: SHELL_WHITE,
        border: `1px solid ${alpha('sandy', 0.2)}`,
        opacity: started ? 1 : 0,
        transform: started ? 'translateY(0)' : 'translateY(16px)',
        transition: `opacity ${CARD_FADE_DURATION}s ease ${cardDelay}s, transform ${CARD_FADE_DURATION}s ease ${cardDelay}s`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: s.padding < 14 ? 5 : 8 }}>
        <div
          style={{
            width: s.iconWrap,
            height: s.iconWrap,
            flexShrink: 0,
            borderRadius: s.iconWrap >= 22 ? 7 : 6,
            backgroundColor: alpha(card.isNegative ? 'coral' : 'green', 0.15),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon style={{ width: s.iconSize, height: s.iconSize, color: accent }} />
        </div>
        <span style={{ fontFamily: 'DM Sans', fontSize: s.labelSize, fontWeight: 600, color: TWILIGHT_NAVY }}>
          {card.label}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: s.gap }}>
        {rows.map((row, ri) => (
          <div key={ri} style={{ display: 'flex', gap: s.gap }}>
            {row.map((v, ci) => {
              const dotIndex = ri * 7 + ci;
              return (
                <div
                  key={ci}
                  style={{
                    width: s.dot,
                    height: s.dot,
                    flexShrink: 0,
                    borderRadius: '50%',
                    background:
                      v === null ? 'transparent' : v === 'empty' ? alpha('sandy', 0.2) : alpha(card.isNegative ? 'coral' : 'green', v),
                    ...(v !== null && {
                      animation: 'dotCalendarPop 0.3s ease forwards',
                      animationDelay: `${dotsStartAt + dotIndex * DOT_STAGGER}s`,
                      animationPlayState: started ? 'running' : 'paused',
                    }),
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

// variant: 'desktop' | 'mobile'. layout: 'grid' (2x2, stacked rows) | 'row'
// (all 4 side by side) — the full-bleed Hero uses 'row' along the bottom
// edge of the photo; 'grid' is kept for any 2x2 use elsewhere.
// startDelay: seconds to hold before this cluster's own entrance begins at
// all (e.g. desktop Hero passes the Mood Board's total animation length, so
// the two don't animate simultaneously).
const DotCalendarCluster = ({ variant = 'desktop', layout = 'grid', startDelay = 0, style }) => {
  const s = SIZES[variant];

  if (layout === 'row') {
    return (
      <div style={{ display: 'flex', gap: s.clusterGap, ...style }}>
        <style>{DOT_POP_KEYFRAMES}</style>
        {DOT_CARDS.map((card, i) => (
          <DotCard key={card.key} card={card} s={s} index={i} startDelay={startDelay} />
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: s.clusterGap, ...style }}>
      <style>{DOT_POP_KEYFRAMES}</style>
      <div style={{ display: 'flex', gap: s.clusterGap }}>
        <DotCard card={DOT_CARDS[0]} s={s} index={0} startDelay={startDelay} />
        <DotCard card={DOT_CARDS[1]} s={s} index={1} startDelay={startDelay} />
      </div>
      <div style={{ display: 'flex', gap: s.clusterGap }}>
        <DotCard card={DOT_CARDS[2]} s={s} index={2} startDelay={startDelay} />
        <DotCard card={DOT_CARDS[3]} s={s} index={3} startDelay={startDelay} />
      </div>
    </div>
  );
};

export default DotCalendarCluster;
